import { useEffect, useState, lazy, Suspense } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { useJsonLd } from "@/hooks/useJsonLd";
import { routeByPath, faqJsonLd } from "@/seo/routes";
import { useZuzuSession } from "./useZuzuSession";
import { ZuzuSplash } from "@/components/ZuzuSplash";

// Design system styles — load in the same order as the prototype's index.html.
// webapp.css/app.css/room.css/session.css/chatbot.css move with their
// components (see webapp.jsx / chatbot.jsx) so marketing-site visitors
// don't download booking-app and chat-widget styles up front.
import "./styles/tokens.css";
import "./styles/shell.css";
import "./styles/site.css";
import "./styles/signup.css";
// franchise.css only carries the "where to find us" single-location card
// styles now (the old franchise-page section was trimmed as dead weight).
import "./styles/franchise.css";
// webapp/app/room/session css live in the lazy webapp chunk (see webapp.jsx)
// and chatbot.css in the lazy chatbot chunk — none of them should block the
// marketing site's first render.
import "./styles/auth-mobile.css";
import "./styles/mobile.css";
import "./styles/anim.css";
// Scoped palette fix — re-asserts the redesign's hex --zuzu-* tokens inside
// .sc-shell so they win over index.css's HSL-triplet definitions of the same
// names (which the redesign consumes directly as colours). Must load last.
import "./styles/token-bridge.css";

// Motion side effects (self-initialising IIFEs).
import "./anim.js";
import "./parallax.js";

import {
  SitePage,
  PALETTE_TO_MOOD,
  MOODS,
  MANIFESTO_TEXTURES,
  NAV_STYLES,
  TWEAK_DEFAULTS,
} from "./zuzuPrototype";
import { ZZ_SITE_PATHS, ZZ_LIVE_DEFAULTS } from "./data";
import { APP_PATHS } from "./appPaths";

// Code-split boundary: ZuzuAppRuntime carries the whole booking-app side —
// the window.__ZZ_ACTIONS surface (PayFast checkout, booking RPCs, auth,
// face entry, InBody scans) plus the signed-in app shell (auth/verify/
// biometric gates → WebApp). The marketing site's eager bundle stays free
// of all of it; see mountRuntime below for when it loads.
const ZuzuAppRuntime = lazy(() => import("./ZuzuAppRuntime"));

// Code-split boundary: the chat widget carries its own system-prompt text
// and network logic that most visitors never open, so it loads on demand
// rather than blocking the initial marketing-site bundle.
const ZuzuChat = lazy(() =>
  import("./chatbot").then((m) => ({ default: m.ZuzuChat })),
);
// WaAuth pulls in the entire 5-step signup wizard (health screening modal,
// face capture, DOB/dial-code pickers) but only renders once a signed-out
// visitor reaches an app route — every marketing-page visitor was paying
// for it up front (Lighthouse "reduce unused JavaScript").
const WaAuth = lazy(() => import("./WaAuth"));

/** Mount children only after the first user interaction (scroll/tap/key).
 *  The chat launcher is pure dead weight for a visitor who bounces — and on
 *  every Lighthouse trace — so it stays unfetched until someone engages. */
function AfterInteraction({ children }: { children: React.ReactNode }) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (ready) return;
    const arm = () => setReady(true);
    const evs = ["scroll", "keydown", "mousemove", "touchstart", "click"];
    evs.forEach((e) =>
      window.addEventListener(e, arm, { passive: true, once: true }),
    );
    return () => evs.forEach((e) => window.removeEventListener(e, arm));
  }, [ready]);
  return ready ? <>{children}</> : null;
}

/* ── Fixed brand settings (the design's chosen defaults; the prototype's
   Tweaks panel is a design-time tool and is intentionally dropped here). ── */
const PALETTE = TWEAK_DEFAULTS.palette as string[];
const HERO_LAYOUT = (TWEAK_DEFAULTS.heroLayout as string) || "portal";
const PHOTO_VARIANT = (TWEAK_DEFAULTS.photoVariant as number) ?? 0;
const NAV_STYLE = (TWEAK_DEFAULTS.navStyle as string) || "clay";
const WAVE_STYLE = (TWEAK_DEFAULTS.waveStyle as string) || "dune";
const WAVE_MOTION = (TWEAK_DEFAULTS.waveMotion as string) || "flow";
const PARALLAX = (TWEAK_DEFAULTS.parallax as string) || "soft";
const MANIFESTO_TEXTURE =
  (TWEAK_DEFAULTS.manifestoTexture as string) || "calm-water";
const TED_SKIN = (TWEAK_DEFAULTS.tedSkin as string) || "auto";

/* ── Route maps — design page/screen ids ⇄ app URLs ── */
// Single source of truth shared with the site.jsx nav/footer hrefs, so the
// two can never drift apart. (APP_PATHS lives in appPaths.ts, shared with
// the lazy runtime chunk.)
const SITE_PATHS: Record<string, string> = ZZ_SITE_PATHS;

type Props = {
  view: "site" | "app";
  page?: string;
  appScreen?: string;
};

export default function ZuzuRedesign({
  view,
  page = "home",
  appScreen = "today",
}: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  // Pages that render a live "N open now" count need real capacity whether or
  // not anyone is signed in — see the poll in useZuzuSession.
  const showsLiveCapacity =
    page === "book" || page === "modalities" || page.startsWith("modality");
  const sessionInfo = useZuzuSession({ liveCapacity: showsLiveCapacity });

  // Merge real live capacity into the design's "right now" status, keeping its
  // temp/sub/total copy and only replacing the free count per room. Mutates the
  // shared data.js singleton in place, which is how app.jsx and room.jsx see it
  // — they hold the same object reference through their own direct imports.
  //
  // Done here rather than inside ZuzuAppRuntime because the runtime renders
  // AFTER the site pages: mutating from there left /book rendering one frame
  // behind the counts, and rendering the hardcoded defaults for signed-out
  // visitors entirely. Synchronous during render, so the screens' useState
  // initialisers below already see the live numbers.
  const liveRooms = ZZ_LIVE_DEFAULTS as Record<string, { free: number }>;
  Object.keys(sessionInfo.liveFree).forEach((id) => {
    if (liveRooms[id])
      liveRooms[id] = { ...liveRooms[id], free: sessionInfo.liveFree[id] };
  });

  // Scroll to a #hash anchor (e.g. /legal#house-rules from the signup waiver)
  // once the page content has rendered — the SPA otherwise ignores hashes.
  // Polls for the element instead of a one-shot delay: on a cold load (the
  // waiver opens /legal#house-rules in a new tab) the section renders later
  // than any fixed timeout, and a missed scroll strands the reader at the top
  // of the page — on the terms section, not the one they tapped.
  useEffect(() => {
    const hash = location.hash?.slice(1);
    if (!hash) return;
    let tries = 0;
    const t = setInterval(() => {
      const el = document.getElementById(hash);
      tries += 1;
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
        clearInterval(t);
      } else if (tries >= 40) {
        clearInterval(t); // give up after ~4s — anchor doesn't exist
      }
    }, 100);
    return () => clearInterval(t);
  }, [location.hash, page, view]);

  // Per-route SEO. ZuzuRedesign renders the marketing routes, so it owns the
  // document title/description/canonical + FAQPage schema on client-side
  // navigation. The same values are baked into the prerendered HTML (via
  // scripts/seo/prerender.mjs) for first byte and non-JS crawlers; both read
  // the shared source of truth in src/seo/routes.ts so they never drift.
  const matchedSeo = routeByPath[location.pathname];
  const seo = matchedSeo ?? routeByPath["/"];
  useSEO({
    title: seo.title,
    description: seo.description,
    keywords: seo.keywords,
    canonical: matchedSeo ? matchedSeo.path : location.pathname,
  });
  useJsonLd(
    "faq-jsonld",
    matchedSeo?.faqs?.length ? faqJsonLd(matchedSeo.faqs) : null,
  );

  // Master brand mood — drives the whole palette via a class on <html>.
  useEffect(() => {
    const el = document.documentElement;
    MOODS.forEach((m: string) => el.classList.remove("mood-" + m));
    const mood = PALETTE_TO_MOOD[JSON.stringify(PALETTE)] || "human";
    el.classList.add("mood-" + mood);
    el.style.setProperty(
      "--manifesto-texture",
      MANIFESTO_TEXTURES[MANIFESTO_TEXTURE] || MANIFESTO_TEXTURES["slow-sand"],
    );
    el.setAttribute(
      "data-navstyle",
      NAV_STYLES.includes(NAV_STYLE) ? NAV_STYLE : "ink",
    );
    el.setAttribute("data-wave", WAVE_STYLE);
    el.setAttribute("data-wavemotion", WAVE_MOTION);
    el.setAttribute("data-parallax", PARALLAX);
    el.setAttribute("data-tedskin", TED_SKIN);
  }, []);

  // Scroll to top on any route change (matches the prototype's view switch).
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, [view, page, appScreen]);

  // In-page custom nav events (announce bar, inline links → zz-nav).
  useEffect(() => {
    const onCustom = (e: Event) => {
      const detail = (e as CustomEvent).detail as string;
      if (detail === "app") return navigate(APP_PATHS.today);
      if (typeof detail === "string" && detail.startsWith("app:")) {
        // The pricing page's gift-card CTAs target the wallet's gift tab —
        // an app screen. Signed out, /wallet is the app's full-bleed phone
        // login (jarring on a desktop marketing visit), so those visitors go
        // through the web signup instead; members land on the gift tab.
        if (detail === "app:wallet" && !sessionInfo.session) {
          return navigate(SITE_PATHS["signup:monthly"]);
        }
        return navigate(APP_PATHS[detail.slice(4)] || APP_PATHS.today);
      }
      if (detail && typeof detail === "string") {
        navigate(SITE_PATHS[detail] || "/");
      }
    };
    window.addEventListener("zz-nav", onCustom as EventListener);
    return () =>
      window.removeEventListener("zz-nav", onCustom as EventListener);
  }, [navigate, sessionInfo.session]);

  // When to load the app runtime (the __ZZ_ACTIONS surface + booking app):
  // - app view: it IS the page.
  // - signup/contact pages: their forms call __ZZ_ACTIONS on submit, so the
  //   chunk must be in flight before the member finishes typing.
  // - the booking page: its slot grid reads live availability through
  //   __ZZ_ACTIONS on first render, not on an interaction, so waiting would
  //   show the page with no capacity counts at all.
  // - a signed-in member on any page: the runtime runs their session beeps,
  //   room-now polling and pending-face flush.
  // - otherwise (anonymous marketing visit): defer to the first interaction —
  //   every action consumer is interaction-driven and null-guards, so nothing
  //   is lost, and the homepage's initial load stays free of the whole chunk
  //   (same pattern as the GTM idle-loader in index.html).
  const needsRuntimeNow =
    view === "app" ||
    page.startsWith("signup") ||
    page === "contact" ||
    page === "book" ||
    !!sessionInfo.session;
  const [interacted, setInteracted] = useState(false);
  useEffect(() => {
    if (needsRuntimeNow || interacted) return;
    const arm = () => setInteracted(true);
    const events = ["pointerdown", "keydown", "scroll", "touchstart"];
    events.forEach((e) =>
      window.addEventListener(e, arm, { passive: true, once: true }),
    );
    return () => events.forEach((e) => window.removeEventListener(e, arm));
  }, [needsRuntimeNow, interacted]);
  const mountRuntime = needsRuntimeNow || interacted;

  const onNav = (p: string) => navigate(SITE_PATHS[p] || "/");
  const goBack = (target: string) => navigate(SITE_PATHS[target] || "/");
  const onView = (v: string) => navigate(v === "app" ? APP_PATHS.today : "/");
  const onApp = () => navigate(APP_PATHS.today);
  const onAuth = () => navigate(APP_PATHS.auth);
  const onScreen = (s: string) => navigate(APP_PATHS[s] || APP_PATHS.today);
  const onEnterApp = (target: string, plan: unknown) =>
    navigate(APP_PATHS[target || "today"], { state: { plan } });

  if (view === "app") {
    // The runtime owns the whole app view (loading/auth/verify gates →
    // WebApp). The fallback mirrors its session-loading state so the lazy
    // load is indistinguishable from the auth check that follows it.
    return (
      <Suspense fallback={<ZuzuSplash />}>
        <ZuzuAppRuntime
          view="app"
          appScreen={appScreen}
          photoVariant={PHOTO_VARIANT}
          sessionInfo={sessionInfo}
        />
      </Suspense>
    );
  }

  return (
    <>
      <div className="sc-shell">
        <main className="sc-main">
          <div className="sc-view">
            <SitePage
              page={page}
              heroLayout={HERO_LAYOUT}
              photoVariant={PHOTO_VARIANT}
              onNav={onNav}
              onApp={onApp}
              onAuth={onAuth}
              goBack={goBack}
              navDir="fwd"
              onScreen={onScreen}
              loggedIn={false}
              onEnterApp={onEnterApp}
              view="site"
              onView={onView}
            />
          </div>
        </main>
        <AfterInteraction>
          <Suspense fallback={null}>
            <ZuzuChat pageContext={page} launcher={true} />
          </Suspense>
        </AfterInteraction>
      </div>
      {mountRuntime && (
        <Suspense fallback={null}>
          <ZuzuAppRuntime
            view="site"
            photoVariant={PHOTO_VARIANT}
            sessionInfo={sessionInfo}
          />
        </Suspense>
      )}
    </>
  );
}
